import { useSelector } from 'react-redux';
import { readStates } from '@/constants/lifecycle';
import { findResultByEvent } from '@/utils/resultsHelper';
import pagingWithFilteredResults from '@/constants/states';
import allowSubscribersRead from '../helper';

const useSubscribersActions = ({ category }) => {
  const { role } = useSelector(({ auth }) => auth);
  return { allowRefresh: allowSubscribersRead(category, role) };
};

const basicSubscribersPanelState = ({
  results: subscribers, name, readErrors, readStatus,
}) => ({
  subscribers, name, readErrors, readStatus,
});

const subscribersAccessControl = ({
  permissions: { roles: { read: allowRoleLink }, users: { read: allowUserLink } },
}) => ({ allowRoleLink, allowUserLink });

const findSubscribers = (subscribers, category, code, event) => (
  findResultByEvent(subscribers, { category, code }, event) || {
    name: '', ...readStates, ...pagingWithFilteredResults(), isDefault: true,
  }
);

const subscribersPanelPaging = ({ currentPage, totalPages: pageCount, pageSize }) => ({
  currentPage, pageCount, pageSize,
});

const subscribersPanelState = (category, code, event, subscribers, role) => {
  const item = findSubscribers(subscribers, category, code, event);
  return {
    item,
    state: {
      ...subscribersAccessControl(role),
      ...basicSubscribersPanelState(item),
      ...subscribersPanelPaging(item),
    },
  };
};

const useSubscribersPanelNarrow = ({ category, code, event }) => useSelector(({
  auth: { role },
  notifications: { subscribers },
}) => subscribersPanelState(category, code, event, subscribers, role)).state;

const useSubscribersTable = ({ category, code, event }) => useSelector(({
  auth: { role },
  notifications: { subscribers },
}) => {
  const { item, state } = subscribersPanelState(category, code, event, subscribers, role);
  return { ...state, total: item.count };
});

const useSubscribersPanelContainer = ({ category, code, event }) => useSelector(({
  auth: { role },
  notifications: { subscribers },
}) => {
  const item = findSubscribers(subscribers, category, code, event);

  return {
    exists: !item.isDefault,
    canRead: item.isDefault ? false : allowSubscribersRead(category, role),
    ...basicSubscribersPanelState(item),
    pageSize: subscribersPanelPaging(item).pageSize,
    total: item.count,
  };
});

const useSubscribersFilters = ({ category, code, event }) => useSelector(({
  auth: { role: { permissions: { roles: { read: allowRoleLink } } } },
  notifications: { subscribers },
}) => {
  const { readStatus, filter: filterTerm, role: roleName } = findSubscribers(subscribers, category, code, event);
  return {
    allowRoleLink, readStatus, filterTerm, roleName,
  };
});

export default {
  useSubscribersActions,
  useSubscribersFilters,
  useSubscribersPanelContainer,
  useSubscribersPanelNarrow,
  useSubscribersTable,
};
